/**
 * 会话日志服务
 * 从 AZ_AI_MVR_H5/src/services/sessionLog.ts 迁移
 */
import request, { handleUnauthorized, TOKEN_KEY } from "../../utils/request";

/** 搜索会话日志 */
export function searchSessionLog(params: Record<string, any>) {
  return request.post("/project/sessionlog/searchsessionlog", params).then((r) => r.data);
}

/**
 * SSE 流式事件类型（与后端 SessionLogController.Create 保持一致）。
 * 后端“透传”上游 PikeRAG V3 的原始 SSE 事件（不改写事件名与字段），
 * 帧形状统一为 SSE：event: <name>\n data: <json>\n\n。
 * 事件名直接来自上游：delta / thinking / status / progress / trace / references /
 * stream.started / stream.completed / stream.error；
 * 另含后端注入的 session（首）/ answer_saved（尾）两帧。
 */
export interface SessionLogStreamHandlers {
  /** 后端写入问题记录后下发：含 ConversationID、问题 SessionLog ID（event: session） */
  onSession?: (evt: {
    conversation_id: string;
    question_session_log_id: string;
    is_new_conversation?: boolean;
  }) => void;
  /** 正文（答案）文本增量（event: delta，data.delta） */
  onContent?: (delta: string, evt: any) => void;
  /** 推理 token（思考过程）（event: thinking，data.delta） */
  onReasoning?: (evt: any) => void;
  /** 阶段提示（event: status，data.message；人类可读，仅展示） */
  onStatus?: (evt: { message: string }) => void;
  /** 进度提示（event: progress，data.message；人类可读，仅展示） */
  onProgress?: (evt: { message: string }) => void;
  /** Agent 执行轨迹（event: trace，data.data；tool_call / tool_result 等结构化数据） */
  onTrace?: (evt: { trace: any }) => void;
  /** 工具/检索事件（兼容旧版历史记录中的 tool 步骤；流式不再下发） */
  onTool?: (evt: any) => void;
  /** 引用列表（event: references，保持 documentId/segment 等原生字段） */
  onReferences?: (evt: { references: any[] }) => void;
  /** 完成事件（event: stream.completed，含 status/finishReason/usage/timing） */
  onCompleted?: (evt: any) => void;
  /** 错误事件（event: stream.error，data.error={code,message}；含控制器层 4xx/5xx） */
  onStreamError?: (err: { code?: any; message?: string }) => void;
  /** 答案 SessionLog 落库后下发的完成信号（event: answer_saved，含 ID、执行码/错误信息、失败兜底答案） */
  onAnswerSaved?: (evt: {
    session_log_id: string;
    execution_code?: number;
    execution_message?: string;
    answer?: string;
  }) => void;
  /** 未知事件（兜底，携带 {event, data}） */
  onEvent?: (evt: { event: string; data: any }) => void;
  /** 流异常（非 AbortError，传输/网络层） */
  onError?: (error: unknown) => void;
  /** 流正常结束（收到 [DONE] 或连接关闭） */
  onDone?: () => void;
  /** 外部取消流 */
  signal?: AbortSignal;
}

export interface CreateSessionLogStreamParams {
  question: string;
  conversationId?: string | null;
  isValid?: boolean;
  references?: any[];
  HistorySessionLogIdList?: string[];
  /**
   * General Docs 胶囊本次生效的分类 ID（DocCategory.ID）。
   * 空数组 = 该胶囊被关闭，不检索 General 来源；undefined = 不参与筛选，后端按全部授权分类检索。
   */
  DocCategoryIds?: string[];
  /** Study Source 胶囊本次生效的 SourcePathStudy.ID；空数组 = 胶囊关闭。 */
  StudySourceIds?: string[];
  /** Personal Docs 胶囊独立总开关；默认 false。 */
  IncludePersonalDocs?: boolean;
  /** true=仅显式文档；false=显式文档与三个库范围取并集。 */
  OnlyUseSelectedSources: boolean;
}

/**
 * 以 SSE 流式方式创建会话日志（问答）。
 * 后端：POST /project/sessionlog/create（返回 text/event-stream）
 * 前端通过 fetch + ReadableStream 解析 SSE，回调形式分发各类事件。
 */
export async function createSessionLogStream(
  params: CreateSessionLogStreamParams,
  handlers: SessionLogStreamHandlers = {},
): Promise<void> {
  const {
    question,
    conversationId,
    references,
    isValid = true,
    HistorySessionLogIdList,
    DocCategoryIds,
    StudySourceIds,
    IncludePersonalDocs = false,
    OnlyUseSelectedSources,
  } = params;

  const body: Record<string, any> = {
    Entity: {
      Question: question,
      IsValid: isValid,
      ...(conversationId ? { ConversationID: conversationId } : {}),
      ...(Array.isArray(references) && references.length > 0 ? { references } : {}),
    },
  };
  // HistorySessionLogIdList 是 SessionLogVM 顶层字段（不在 SessionLog 实体内），
  // 必须放在 body 顶层而不是 body.Entity，否则后端模型绑定会丢弃，导致 session_history 为空
  if (Array.isArray(HistorySessionLogIdList) && HistorySessionLogIdList.length > 0) {
    body.HistorySessionLogIdList = HistorySessionLogIdList;
  }
  // 同为 SessionLogVM 顶层字段。空数组是有意义的取值（「不检索 General 来源」），
  // 故只在 undefined 时省略，不能按 length 判空。
  if (Array.isArray(DocCategoryIds)) {
    body.DocCategoryIds = [...new Set(DocCategoryIds)];
  }
  if (Array.isArray(StudySourceIds)) {
    body.StudySourceIds = [...new Set(StudySourceIds)];
  }
  body.IncludePersonalDocs = IncludePersonalDocs;
  body.OnlyUseSelectedSources = OnlyUseSelectedSources;

  const token = localStorage.getItem(TOKEN_KEY);
  const baseURL = (request.defaults?.baseURL as string | undefined) || "/api";
  const url = `${baseURL.replace(/\/$/, "")}/project/sessionlog/create`;

  let response: Response;
  try {
    response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
        "Accept-Language": "en-US",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
      signal: handlers.signal,
    });
  } catch (err: any) {
    if (err?.name === "AbortError") {
      handlers.onDone?.();
      return;
    }
    handlers.onError?.(err);
    return;
  }

  if (!response.ok) {
    if (response.status === 401) {
      await handleUnauthorized();
    }
    let errText = "";
    try {
      errText = await response.text();
    } catch {
      /* ignore */
    }
    handlers.onError?.(new Error(`HTTP ${response.status}: ${errText || response.statusText}`));
    return;
  }

  if (!response.body) {
    handlers.onError?.(new Error("Empty SSE response body"));
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";
  let terminated = false;

  // 按上游事件名分发；data 为该帧的 JSON 载荷。
  // 为复用既有 handler 形状，这里对 thinking/trace 做最小适配（不改写 references 字段）。
  const dispatch = (eventName: string, data: any) => {
    switch (eventName) {
      case "session":
        handlers.onSession?.(data);
        break;
      case "delta":
        handlers.onContent?.(String(data?.delta ?? ""), data);
        break;
      case "thinking":
        handlers.onReasoning?.({ event: "llm.delta", reasoning: String(data?.delta ?? "") });
        break;
      case "status":
        handlers.onStatus?.({ message: String(data?.message ?? "") });
        break;
      case "progress":
        handlers.onProgress?.({ message: String(data?.message ?? "") });
        break;
      case "trace":
        handlers.onTrace?.({ trace: data && data.data != null ? data.data : data });
        break;
      case "tool":
        handlers.onTool?.(data);
        break;
      case "references":
        handlers.onReferences?.({
          references: Array.isArray(data?.references) ? data.references : [],
        });
        break;
      case "stream.completed":
        handlers.onCompleted?.(data);
        break;
      case "stream.error":
        handlers.onStreamError?.(data?.error ?? data ?? {});
        break;
      case "answer_saved":
        handlers.onAnswerSaved?.(data);
        break;
      case "stream.started":
      case "debug.agent-config":
        // 仅透传给兜底回调，前端默认不展示
        handlers.onEvent?.({ event: eventName, data });
        break;
      default:
        handlers.onEvent?.({ event: eventName, data });
        break;
    }
  };

  try {
    while (!terminated) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // SSE 事件以空行 \n\n 分隔，逐事件解析
      let sepIndex: number;
      while ((sepIndex = buffer.indexOf("\n\n")) !== -1) {
        const rawEvent = buffer.slice(0, sepIndex);
        buffer = buffer.slice(sepIndex + 2);
        if (!rawEvent.trim()) continue;

        // 解析 SSE 帧：event: <name> + 多行 data: 拼接（忽略 ":" 注释行，如心跳）
        let eventName = "";
        const dataLines: string[] = [];
        for (const line of rawEvent.split("\n")) {
          if (line.startsWith("event:")) eventName = line.slice(6).trim();
          else if (line.startsWith("data:")) dataLines.push(line.slice(5).replace(/^\s/, ""));
        }
        if (dataLines.length === 0) continue;

        const dataStr = dataLines.join("\n");
        if (dataStr === "[DONE]") {
          terminated = true;
          break;
        }
        let data: any;
        try {
          data = JSON.parse(dataStr);
        } catch (parseErr) {
          console.warn("[SSE] JSON parse failed for data:", dataStr, parseErr);
          continue;
        }
        dispatch(eventName, data);
      }
    }
    handlers.onDone?.();
  } catch (err: any) {
    if (err?.name === "AbortError") {
      handlers.onDone?.();
      return;
    }
    handlers.onError?.(err);
  } finally {
    try {
      reader.releaseLock();
    } catch {
      /* ignore */
    }
  }
}

/** 答案反馈报告 */
export function sessionAnswerReport(data: any, options?: { getResponse?: boolean }) {
  if (options?.getResponse) {
    return request.post("/project/sessionlog/sessionanswerreport", data).then((r) => ({
      data: r.data,
      response: r,
    }));
  }
  return request.post("/project/sessionlog/sessionanswerreport", data).then((r) => r.data);
}

/** 清除已保存的翻译内容（TranslatedSummary/Answer/Rationale 置空） */
export function clearTranslate(sessionLogId: string) {
  return request
    .post(`/project/sessionlog/cleartranslate?sessionLogId=${encodeURIComponent(sessionLogId)}`)
    .then((r) => r.data);
}

export interface TranslateChunk {
  type: "field" | "token" | "field_done" | "done";
  field?: "summary" | "answer" | "rationale";
  token?: string;
}

/**
 * 流式翻译：通过 SSE 逐 token 返回翻译内容。
 * onChunk 每次收到增量时调用，返回 cleanup 函数供外部取消。
 *
 * 注意：使用 fetch 而非 EventSource，因为 EventSource 不支持自定义请求头（无法附加 Authorization）。
 */
export function chenTranslateStream(
  sessionLogId: string,
  onChunk: (chunk: TranslateChunk) => void,
  onError?: (err: unknown) => void,
): () => void {
  const controller = new AbortController();

  (async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    const baseURL = (request.defaults?.baseURL as string | undefined) || "/api";
    const url = `${baseURL.replace(/\/$/, "")}/project/sessionlog/chentranslate?sessionLogId=${encodeURIComponent(sessionLogId)}`;

    let response: Response;
    try {
      response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "text/event-stream",
          "Accept-Language": "en-US",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        signal: controller.signal,
      });
    } catch (err: any) {
      if (err?.name !== "AbortError") onError?.(err);
      return;
    }

    if (!response.ok || !response.body) {
      if (response.status === 401) {
        await handleUnauthorized();
      }
      onError?.(new Error(`HTTP ${response.status}`));
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let buffer = "";
    let currentField: TranslateChunk["field"] | undefined;

    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let sepIndex: number;
        while ((sepIndex = buffer.indexOf("\n\n")) !== -1) {
          const rawEvent = buffer.slice(0, sepIndex);
          buffer = buffer.slice(sepIndex + 2);
          if (!rawEvent.trim()) continue;

          let eventType = "message";
          const dataLines: string[] = [];
          for (const line of rawEvent.split("\n")) {
            if (line.startsWith("event:")) eventType = line.slice(6).trim();
            else if (line.startsWith("data:")) dataLines.push(line.slice(5).replace(/^ /, ""));
          }
          const dataStr = dataLines.join("\n");

          if (eventType === "field") {
            currentField = dataStr as TranslateChunk["field"];
            onChunk({ type: "field", field: currentField });
          } else if (eventType === "field_done") {
            onChunk({ type: "field_done", field: dataStr as TranslateChunk["field"] });
          } else if (eventType === "done") {
            onChunk({ type: "done" });
            return;
          } else {
            // default message event → token
            onChunk({ type: "token", field: currentField, token: dataStr.replace(/\\n/g, "\n") });
          }
        }
      }
    } catch (err: any) {
      if (err?.name !== "AbortError") onError?.(err);
    } finally {
      try {
        reader.releaseLock();
      } catch {
        /* ignore */
      }
    }
  })();

  return () => controller.abort();
}
